/**
 * Service de gestion des paramètres de debug pour les projets.
 *
 * Communique avec l'API backend pour charger/sauvegarder les paramètres
 * du debug panel spécifiques à chaque projet.
 */

import { useAuthStore } from "@/stores/authStore";

const API_BASE_URL = (import.meta.env.VITE_API_URL || "").replace(
  /\/api\/?$/,
  "",
);

/**
 * Paramètres de debug persistables (doivent correspondre au backend)
 */
export interface DebugSettings {
  // Scene diagnostics
  hdrActif: boolean;
  gammaScene: number;
  brouillardActif: boolean;
  fxaaActif: boolean;
  soleilVisible: boolean;
  atmosphereVisible: boolean;
  eclairageGlobeActif: boolean;
  ombresSceneActives: boolean;
  ombresTerrain: "DISABLED" | "ENABLED" | "CAST_ONLY" | "RECEIVE_ONLY";
  modeLumiere: "directional" | "sun";
  intensiteLumiere: number;
  couleurLumiere: "white" | "warm" | "cool";
  ombresBasemap: "DISABLED" | "ENABLED" | "CAST_ONLY" | "RECEIVE_ONLY";

  // Basemap primitives
  backFaceCulling: boolean;
  debugWireframe: boolean;
  debugColorizeTiles: boolean;
  debugSurfaceAplatie: boolean;
  modeShaderBasemap: "none" | "lit_sdr" | "unlit" | "gain";
  gainCouleur: number;
  facteurIblLitSdr: number;
  intensiteLumiereBasemapLitSdr: number;
  plancherLumineuxLit: number;
  plancherLumineuxDynamiqueActif: boolean;
  plancherLumineuxDynamiqueMin: number;
  plancherLumineuxDynamiqueMax: number;
  attenuationNocturneBasemapActive: boolean;
  facteurIblBasemapNuit: number;
  intensiteBasemapNuit: number;
  plancherLumineuxBasemapNuit: number;
  intensiteLumiereSceneNuit: number;
  intensiteLumiereArchitecte: number;
  intensiteLumierePaysage: number;

  // System settings
  oitEnabled: boolean;
  showFlattenDebug: boolean;
}

/**
 * Valeurs par défaut des paramètres de debug
 */
export function getDefaultDebugSettings(): DebugSettings {
  return {
    // Scene
    hdrActif: false,
    gammaScene: 1.0,
    brouillardActif: false,
    fxaaActif: false,
    soleilVisible: true,
    atmosphereVisible: true,
    eclairageGlobeActif: false,
    ombresSceneActives: true,
    ombresTerrain: "DISABLED",
    modeLumiere: "directional",
    intensiteLumiere: 1.0,
    couleurLumiere: "white",
    ombresBasemap: "DISABLED",

    // Basemap
    backFaceCulling: false,
    debugWireframe: false,
    debugColorizeTiles: false,
    debugSurfaceAplatie: false,
    modeShaderBasemap: "lit_sdr",
    gainCouleur: 1.0,
    facteurIblLitSdr: 1.0,
    intensiteLumiereBasemapLitSdr: 1.0,
    plancherLumineuxLit: 0.05,
    plancherLumineuxDynamiqueActif: true,
    plancherLumineuxDynamiqueMin: 0.02,
    plancherLumineuxDynamiqueMax: 0.15,
    attenuationNocturneBasemapActive: true,
    facteurIblBasemapNuit: 0.3,
    intensiteBasemapNuit: 0.5,
    plancherLumineuxBasemapNuit: 0.02,
    intensiteLumiereSceneNuit: 0.08,
    intensiteLumiereArchitecte: 1.0,
    intensiteLumierePaysage: 1.0,

    // System
    oitEnabled: false,
    showFlattenDebug: false,
  };
}

/**
 * Effectue une requête API authentifiée
 */
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const token = useAuthStore.getState().token;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    useAuthStore.getState().logout();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Récupère les paramètres de debug d'un projet
 */
export async function getDebugSettings(
  projectId: string,
): Promise<DebugSettings> {
  const response = await fetchApi<{ success: boolean; data: DebugSettings }>(
    `/api/projects/${projectId}/debug-settings`,
  );

  if (response.success && response.data) {
    return response.data;
  }

  return getDefaultDebugSettings();
}

/**
 * Sauvegarde les paramètres de debug d'un projet
 */
export async function saveDebugSettings(
  projectId: string,
  settings: DebugSettings,
): Promise<void> {
  await fetchApi<{ success: boolean }>(`/api/projects/${projectId}/debug-settings`, {
    method: "POST",
    body: JSON.stringify(settings),
  });
}

/**
 * Convertit les paramètres de debug en EtatDiagnosticRendu (avec ShadowMode Cesium)
 * Note: Cette fonction sera utilisée côté composant pour convertir les valeurs
 */
export function convertToShadowMode(
  value: "DISABLED" | "ENABLED" | "CAST_ONLY" | "RECEIVE_ONLY",
): number {
  // Cesium.ShadowMode values
  switch (value) {
    case "DISABLED":
      return 0; // Cesium.ShadowMode.DISABLED
    case "ENABLED":
      return 1; // Cesium.ShadowMode.ENABLED
    case "CAST_ONLY":
      return 2; // Cesium.ShadowMode.CAST_ONLY
    case "RECEIVE_ONLY":
      return 3; // Cesium.ShadowMode.RECEIVE_ONLY
    default:
      return 0;
  }
}

/**
 * Convertit un ShadowMode Cesium en valeur sérialisable
 */
export function convertFromShadowMode(
  shadowMode: number,
): "DISABLED" | "ENABLED" | "CAST_ONLY" | "RECEIVE_ONLY" {
  switch (shadowMode) {
    case 0:
      return "DISABLED";
    case 1:
      return "ENABLED";
    case 2:
      return "CAST_ONLY";
    case 3:
      return "RECEIVE_ONLY";
    default:
      return "DISABLED";
  }
}
